import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';

/**
 * Updates the free-shipping progress bar when the cart changes.
 * On product pages, keep `data-additional-amount` in sync with the selected variant / quantity.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} label
 * @property {HTMLElement} bar
 * @property {HTMLElement} fill
 *
 * @extends {Component<Refs>}
 */
class ShippingProgress extends Component {
  /** @type {number | null} */
  #cartTotal = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    event.promise
      ?.then(async ({ detail }) => {
        const cart = detail?.resource;
        const total = typeof cart?.total_price === 'number' ? cart.total_price : await this.#fetchCartTotal();
        if (typeof total !== 'number') return;
        this.#cartTotal = total;
        this.refresh();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[shipping-progress] Cart update failed:', error);
      });
  };

  async #fetchCartTotal() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) return null;
      const cart = await response.json();
      return typeof cart.total_price === 'number' ? cart.total_price : null;
    } catch {
      return null;
    }
  }

  /**
   * Recalculate progress from current dataset values.
   * Product pages can call this after changing `data-additional-amount`.
   */
  refresh() {
    const threshold = Number(this.dataset.threshold);
    if (!threshold) return;

    const baseAmount =
      this.#cartTotal ??
      Number(this.dataset.amount || 0);
    const additional = Number(this.dataset.additionalAmount || 0);
    const progressAmount = Math.max(0, baseAmount + additional);
    const remaining = Math.max(0, threshold - progressAmount);
    const percent = Math.min(100, (progressAmount / threshold) * 100);
    const reached = progressAmount >= threshold;

    this.classList.toggle('shipping-progress--reached', reached);
    this.dataset.amount = String(baseAmount);

    const { fill, bar, label } = this.refs;
    if (fill) fill.style.width = `${percent}%`;
    if (bar) bar.setAttribute('aria-valuenow', String(Math.round(percent)));
    if (label) label.textContent = this.#message(reached, remaining);
  }

  /**
   * @param {boolean} reached
   * @param {number} remaining
   */
  #message(reached, remaining) {
    if (reached) return this.dataset.messageComplete || '';

    const variant = this.dataset.variant || 'cart';
    if (variant === 'product') {
      return this.#interpolate(this.dataset.messageThreshold, this.#format(Number(this.dataset.threshold)));
    }

    return this.#interpolate(this.dataset.messageRemaining, this.#format(remaining));
  }

  /**
   * @param {string | undefined} template
   * @param {string} amount
   */
  #interpolate(template, amount) {
    return (template || '').replace('__AMOUNT__', amount);
  }

  /**
   * @param {number} cents
   */
  #format(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'EUR';
    return formatMoney(cents, format, currency);
  }
}

if (!customElements.get('shipping-progress')) {
  customElements.define('shipping-progress', ShippingProgress);
}
